import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { SovosBox, SovosTab, SovosTabs, SovosTypography } from "mosaic-react";
import StagingDataGrid from "./StagingDataGrid";
import TranslationsList from "./TranslationsList";
import { APRS_WEBAPP_SOVI_AI_ENABLED } from "./featureFlags";

const SUB_TAB_GRID = "grid";
const SUB_TAB_TRANSLATIONS = "translations";

const StagingDataPanel = ({
    profileId,
    projectId,
    mappingId,
    configSet,
    entityName,
    items = [],
    headerItems = [],
    canMutateTranslations = true,
    nonOwnerTooltip,
    onFetchIssues,
    onFixCell,
    onProceed,
    proceedLabel,
    proceedDisabled,
    onListStagingTranslations,
    onCreateStagingTranslation,
    onUpdateStagingTranslation,
    onToggleStagingTranslationState,
    onSoftDeleteStagingTranslation,
    onApplyStagingTranslation,
}) => {
    const [activeTab, setActiveTab] = useState(SUB_TAB_GRID);
    const [issueSummary, setIssueSummary] = useState(null);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [savingCell, setSavingCell] = useState(false); // eslint-disable-line no-unused-vars

    const SYSTEM_COLUMNS = useMemo(() => new Set(["CLB_ImportID", "FieldMetadata"]), []);

    const customColumnHeaders = useMemo(
        () => headerItems.filter((h) => !SYSTEM_COLUMNS.has(h.headerName)),
        [headerItems, SYSTEM_COLUMNS],
    );

    const customColumnNames = useMemo(
        () => customColumnHeaders.map((h) => h.columnName),
        [customColumnHeaders],
    );

    const refreshIssues = useMemo(() => () => {
        if (!onFetchIssues || !profileId || !projectId || !mappingId || !configSet || !entityName) return;
        setLoadingIssues(true);
        onFetchIssues(profileId, mappingId, configSet, entityName, projectId)
            .then((result) => setIssueSummary(result ? { ...result.summary, rows: result.rows || [] } : null))
            .catch(() => setIssueSummary(null))
            .finally(() => setLoadingIssues(false));
    }, [onFetchIssues, profileId, projectId, mappingId, configSet, entityName]);

    useEffect(() => {
        refreshIssues();
    }, [refreshIssues]);

    const hasMappingContext = !!(mappingId && configSet && entityName);

    return (
        <SovosBox sx={{
            display: "grid",
            gridTemplateColumns: APRS_WEBAPP_SOVI_AI_ENABLED ? "minmax(0, 1fr) 395px" : "minmax(0, 1fr)",
            gap: 0,
            alignItems: "stretch",
            minHeight: "calc(100vh - 280px)",
        }}>
            <SovosBox sx={{ minWidth: 0, p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                <SovosTabs
                    value={activeTab}
                    onChange={(_, value) => setActiveTab(value)}
                >
                    <SovosTab value={SUB_TAB_GRID} label="Staging data" />
                    <SovosTab value={SUB_TAB_TRANSLATIONS} label="Translations" />
                </SovosTabs>

                {activeTab === SUB_TAB_GRID && (
                    !hasMappingContext ? (
                        <SovosBox sx={{
                            p: 4,
                            border: "1px dashed",
                            borderColor: "divider",
                            borderRadius: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                        }}>
                            <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                                Save a mapping first to scan the staging data for issues.
                            </SovosTypography>
                        </SovosBox>
                    ) : loadingIssues && !issueSummary ? (
                        <SovosBox sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1.5,
                            py: 6,
                        }}>
                            <CircularProgress size={28} />
                            <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                                Scanning for data issues…
                            </SovosTypography>
                        </SovosBox>
                    ) : (
                        <StagingDataGrid
                            items={items}
                            columnHeaders={customColumnHeaders}
                            issueSummary={issueSummary}
                            loading={loadingIssues}
                            savingCell={savingCell}
                            profileId={profileId}
                            projectId={projectId}
                            canMutate={canMutateTranslations}
                            nonOwnerTooltip={nonOwnerTooltip}
                            onFixCell={onFixCell}
                            onRefreshIssues={refreshIssues}
                            onProceed={onProceed}
                            proceedLabel={proceedLabel}
                            proceedDisabled={proceedDisabled}
                        />
                    )
                )}

                {activeTab === SUB_TAB_TRANSLATIONS && (
                    <TranslationsList
                        profileId={profileId}
                        projectId={projectId}
                        columnOptions={customColumnNames}
                        canMutate={canMutateTranslations}
                        nonOwnerTooltip={nonOwnerTooltip}
                        onList={onListStagingTranslations}
                        onCreate={onCreateStagingTranslation}
                        onUpdate={onUpdateStagingTranslation}
                        onToggleState={onToggleStagingTranslationState}
                        onSoftDelete={onSoftDeleteStagingTranslation}
                        onApply={onApplyStagingTranslation}
                    />
                )}
            </SovosBox>

            {APRS_WEBAPP_SOVI_AI_ENABLED && (
                /* eslint-disable react/jsx-no-comment-textnodes */
                /*
                    Right-hand drawer slot for AI suggestions. Mounted only when
                    APRS_WEBAPP_SOVI_AI_ENABLED is true. Kept in the layout so the
                    follow-up AI ticket can drop in the suggestion panel without
                    re-grafting the page.
                */
                /* eslint-enable react/jsx-no-comment-textnodes */
                <SovosBox sx={{ minWidth: 0, height: "100%", p: 3, borderLeft: "1px solid", borderColor: "divider" }}>
                    <SovosTypography variant="caption" sx={{ color: "text.disabled" }}>
                        AI suggestions panel
                    </SovosTypography>
                </SovosBox>
            )}
        </SovosBox>
    );
};

export default StagingDataPanel;
